import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './Login.css';
import Navbar from '../Navigation/Navbar';
import {
    loginUser,
    getAllInstitutions,
    getAllUserInstitutions,
    getUserSpecializations,
    createUserSpecialization,
    isAuthenticated,
    checkEmailExists,
} from '../../api/users_institutions';
import { sendVerificationEmail, verifyEmailCode, resetPassword } from '../../api/mail_verification';
import { R2_MEDIA_BASE_URL } from '../../config/mediaConfig';

function Login() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [view, setView] = useState('login');
    const [loginUsername, setLoginUsername] = useState(location.state?.prefillUsername || '');
    const [loginPassword, setLoginPassword] = useState('');
    const [forgotEmail, setForgotEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [forgotError, setForgotError] = useState('');

    const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
    const [verificationState, setVerificationState] = useState('input');
    const verificationTimerRef = useRef(null);

    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [resetError, setResetError] = useState('');
    const [resetLoading, setResetLoading] = useState(false);

    const [institutions, setInstitutions] = useState([]);

    useEffect(() => {
        if (isAuthenticated()) {
            navigate('/dashboard');
        }
    }, [])

    useEffect(() => {
        loadInstitutions();
    }, []);

    const loadInstitutions = async () => {
        try {
            const institutions = await getAllInstitutions();
            setInstitutions(Array.isArray(institutions) ? institutions : []);
        } catch (err) {
            console.error('Failed to fetch institutions:', err);
        }
    };

    const getInstitutionForEmail = useCallback((email) => {
        const emailDomain = String(email || '').split('@')[1]?.toLowerCase();
        if (!emailDomain) {
            return null;
        }

        return institutions.find((inst) => String(inst.domain || '').toLowerCase() === emailDomain) || null;
    }, [institutions]);

    const getRequiredSpecializationDomains = useCallback((emails) => {
        const domainsByKey = new Map();

        emails.forEach((email) => {
            const institution = getInstitutionForEmail(email);
            const targetDomain = String(institution?.type || '').trim();

            if (targetDomain) {
                domainsByKey.set(targetDomain.toLowerCase(), targetDomain);
            }
        });

        return Array.from(domainsByKey.values());
    }, [getInstitutionForEmail]);

    const syncUserSpecializationsForEmails = useCallback(async (user, emails) => {
        if (!user?.id || !Array.isArray(emails) || emails.length === 0) {
            return;
        }

        const requiredTargetDomains = getRequiredSpecializationDomains(emails);
        if (requiredTargetDomains.length === 0) {
            return;
        }

        try {
            const userSpecializations = await getUserSpecializations(user.id);
            const existingTargetDomains = new Set(
                (Array.isArray(userSpecializations) ? userSpecializations : [])
                    .map((specialization) => String(specialization.target_domain || '').trim().toLowerCase())
                    .filter(Boolean)
            );

            for (const targetDomain of requiredTargetDomains) {
                if (!existingTargetDomains.has(targetDomain.toLowerCase())) {
                    await createUserSpecialization(user.id, {
                        user: user.id,
                        target_domain: targetDomain,
                    });
                    existingTargetDomains.add(targetDomain.toLowerCase());
                }
            }
        } catch (err) {
            console.error('Failed to sync user specializations:', err);
        }
    }, [getRequiredSpecializationDomains]);

    const closeLogin = useCallback(() => {
        setError('');
        setForgotError('');
        navigate('/');
    }, [navigate]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setError('');
                setForgotError('');
                navigate('/');
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [navigate]);

    const handleLogin = async () => {
        setError('');
        setLoading(true);

        try {
            const response = await loginUser(loginUsername, loginPassword);
            console.log('Login successful:', response);
            const loggedInUser = response?.user || null;

            if (loggedInUser?.id) {
                const userInstitutions = await getAllUserInstitutions(loggedInUser.id).catch((err) => {
                    console.error('Failed to fetch user institution emails:', err);
                    return [];
                });
                const linkedEmails = (Array.isArray(userInstitutions) ? userInstitutions : [])
                    .map((userInstitution) => userInstitution.email)
                    .filter(Boolean);

                await syncUserSpecializationsForEmails(loggedInUser, linkedEmails);
            }

            navigate('/dashboard');
        } catch (err) {
            console.error('Login failed:', err);
            setError(err?.error || err?.detail || t('login.errors.invalidCredentials'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (verificationTimerRef.current) {
                clearTimeout(verificationTimerRef.current);
            }
        };
    }, []);

    const handleForgotPassword = async () => {
        setForgotError('');

        try {
            const email = forgotEmail.trim();
            if (!email) {
                setForgotError(t('login.errors.enterEmail'));
                return;
            }

            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                setForgotError(t('login.errors.notAnEmail'));
                return;
            }
            
            const emailCheck = await checkEmailExists(email);
            if (!emailCheck?.exists) {
                setForgotError(t('login.errors.emailNotFound', { email }));
                return;
            }

            setVerificationCode(['', '', '', '', '', '']);
            setVerificationState('input');
            sendVerificationEmail(email, i18n.language).catch((err) => {
                console.error('Failed to send verification email:', err);
            });
            setView('verifying');
        } catch (err) {
            console.error('Error occurred while initiating forgot password process:', err);
        }
    };

    const updateVerificationCode = (index, value) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        setVerificationCode((prev) => prev.map((currentValue, currentIndex) => (currentIndex === index ? digit : currentValue)));

        if (digit && index < 5) {
            const nextField = document.getElementById(`reset-code-${index + 1}`);
            if (nextField) {
                nextField.focus();
            }
        }
    };

    const moveToNext = (current, nextFieldID) => {
        if (current && current.value && current.value.length >= 1) {
            if (nextFieldID) {
                const nextField = document.getElementById(nextFieldID);
                if (nextField) {
                    nextField.focus();
                }
            }
        }
    };

    const startVerificationProcess = async () => {
        if (verificationState === 'loading') {
            return;
        }

        const enteredCode = verificationCode.join('');
        setVerificationState('loading');

        let verified = false;
        try {
            const result = await verifyEmailCode(forgotEmail.trim(), enteredCode);
            verified = !!result?.verified;
        } catch (err) {
            console.error('Failed to verify code:', err);
        }

        if (verified) {
            setVerificationState('success');

            verificationTimerRef.current = window.setTimeout(() => {
                verificationTimerRef.current = null;
                setView('resetting');
            }, 1200);
            return;
        }

        setVerificationState('error');

        verificationTimerRef.current = window.setTimeout(() => {
            verificationTimerRef.current = null;
            setVerificationState('input');
            setVerificationCode(['', '', '', '', '', '']);
        }, 1600);
    };

    const handleResetPassword = async () => {
        setResetError('');

        if (!newPassword.trim() || !confirmNewPassword.trim()) {
            setResetError(t('signup.errors.createPassword'));
            return;
        }

        if (newPassword.length < 8) {
            setResetError(t('common.passwordMinLength'));
            return;
        }

        if (newPassword !== confirmNewPassword) {
            setResetError(t('signup.errors.passwordMismatch'));
            return;
        }

        setResetLoading(true);
        try {
            await resetPassword(forgotEmail.trim(), newPassword);
            window.location.href = '/login';
        } catch (err) {
            console.error('Failed to reset password:', err);
            setResetError(t('login.errors.resetFailed'));
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <>
            <Navbar />
            <div className="Login">
                <img src={`${R2_MEDIA_BASE_URL}/login/laugheffect.png`} alt={"Boy looking at login"} className="side-mascot-left" />

                <img src={`${R2_MEDIA_BASE_URL}/login/sneak.png`} alt={"Peeking girl looking up at login"} className="corner-mascot-right" />

                <div className="auth-bg-shapes">
                    <div className="shape shape-purple-1"></div>
                    <div className="shape shape-purple-2"></div>
                    <div className="shape shape-purple-3"></div>
                    <div className="bg-decorations">
                        <div className="dec-icon" style={{ top: '15%', left: '10%', backgroundImage: 'var(--svg-star)', width: '45px', height: '45px', animationDelay: '0s' }}></div>
                        <div className="dec-icon" style={{ top: '25%', right: '15%', backgroundImage: 'var(--svg-sparkle)', width: '50px', height: '50px', animationDelay: '1.5s' }}></div>

                        <div className="dec-icon" style={{ bottom: '20%', left: '25%', backgroundImage: 'var(--svg-glowing-bulb)', width: '60px', height: '60px', animationDelay: '3s', opacity: '0.3' }}></div>

                        <div className="dec-icon" style={{ top: '18%', right: '8%', backgroundImage: 'var(--svg-sparkle)', width: '45px', height: '45px', animationDelay: '2s', opacity: '0.25' }}></div>

                        <div className="dec-icon" style={{ bottom: '30%', right: '12%', backgroundImage: 'var(--svg-star)', width: '40px', height: '40px', animationDelay: '0.8s' }}></div>
                        <div className="dec-icon" style={{ top: '50%', left: '18%', backgroundImage: 'var(--svg-star)', width: '30px', height: '30px', animationDelay: '2.2s', opacity: '0.15' }}></div>

                        <div className="dec-icon" style={{ top: '10%', right: '35%', backgroundImage: 'var(--svg-glowing-bulb)', width: '40px', height: '40px', animationDelay: '1s', opacity: '0.25' }}></div>

                        <div className="dec-icon" style={{ bottom: '10%', left: '45%', backgroundImage: 'var(--svg-sparkle)', width: '35px', height: '35px', animationDelay: '2.5s', opacity: '0.2' }}></div>
                    </div>
                </div>

                <div className="login-card auth-card">
                    <button onClick={closeLogin} className="auth-close" aria-label={t('login.closeDialog')}>&times;</button>
                    <div className="auth-card-top">
                        <div>
                            <h2>
                                {{
                                    login: t('login.welcomeBack'),
                                    forgot: t('login.resetPassword'),
                                    verifying: t('common.verifyYourEmail'),
                                    resetting: t('login.chooseNewPasswordTitle'),
                                }[view]}
                            </h2>
                            <p>
                                {{
                                    login: t('login.loginSubtitle'),
                                    forgot: t('login.resetSubtitle'),
                                    verifying: '',
                                    resetting: t('login.chooseNewPasswordSubtitle'),
                                }[view]}
                            </p>
                        </div>
                    </div>

                    {error && view === 'login' && <div className="auth-alert auth-alert-error">{error}</div>}
                    {forgotError && view === 'forgot' && <div className="auth-alert auth-alert-info">{forgotError}</div>}
                    {resetError && view === 'resetting' && <div className="auth-alert auth-alert-error">{resetError}</div>}

                    {view === 'login' && (
                        <>
                            <input
                                placeholder={t('login.usernameOrEmail')}
                                type="text"
                                className="userInput"
                                value={loginUsername}
                                onChange={(e) => setLoginUsername(e.target.value)}
                                disabled={loading}
                            />
                            <input
                                placeholder={t('login.password')}
                                type="password"
                                className="passInput"
                                value={loginPassword}
                                onChange={(e) => setLoginPassword(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                                disabled={loading}
                            />

                            <div className="auth-links-row">
                                <button type="button" className="auth-link-button" onClick={() => setView('forgot')}>{t('login.forgotPassword')}</button>
                            </div>

                            <button
                                className="btn-primary auth-submit"
                                onClick={handleLogin}
                                disabled={loading || !loginUsername || !loginPassword}
                            >
                                {loading ? t('login.signingIn') : t('login.signIn')}
                            </button>

                            <div className="auth-switch-row">
                                <span>{t('login.noAccount')}</span>
                                <button type="button" className="auth-link-button" onClick={() => navigate('/signup')}>{t('common.signUp')}</button>
                            </div>
                        </>
                    )}

                    {view === 'forgot' && (
                        <>
                            <label>{t('login.institutionEmailLabel')}</label>
                            <input
                                type="email"
                                value={forgotEmail}
                                onChange={(e) => setForgotEmail(e.target.value)}
                                disabled={loading}
                            />

                            <button className="btn-primary auth-submit" onClick={handleForgotPassword} disabled={loading || !forgotEmail}>
                                {t('login.sendResetLink')}
                            </button>

                            <div className="auth-switch-row">
                                <span>{t('login.rememberedPassword')}</span>
                                <button type="button" className="auth-link-button" onClick={() => setView('login')}>{t('common.logIn')}</button>
                            </div>
                        </>
                    )}

                    {view === 'verifying' && (
                        <>
                            <p style={{ color: 'var(--text-muted)', lineHeight: 1.5, fontSize: '0.95rem', alignSelf: 'center', textAlign: 'center' }}>
                                {t('common.enterCodeSentTo')} <br />
                                <strong style={{ color: 'var(--text-main)' }}>{forgotEmail}</strong>
                            </p>

                            {verificationState === 'input' && (
                                <div className="code-inputs">
                                    {verificationCode.map((value, index) => (
                                        <input
                                            key={index}
                                            type="text"
                                            className="code-input"
                                            id={`reset-code-${index}`}
                                            maxLength="1"
                                            value={value}
                                            onChange={(e) => updateVerificationCode(index, e.target.value)}
                                            onKeyUp={(e) => moveToNext(e.target, `reset-code-${index + 1}`)}
                                        />
                                    ))}
                                </div>
                            )}

                            {verificationState === 'loading' && (
                                <div>
                                    <div className="spinner"></div>
                                    <h3 style={{ marginTop: '25px', color: 'var(--text-main)', alignSelf: 'center', textAlign: 'center' }}>{t('common.verifyingCode')}</h3>
                                </div>
                            )}

                            {verificationState === 'success' && (
                                <div>
                                    <div className="icon-pop">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon-svg success-icon" viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 6L9 17l-5-5"></path>
                                        </svg>
                                    </div>
                                    <h3 style={{ marginTop: '20px', color: '#10b981', fontWeight: 800, alignSelf: 'center', textAlign: 'center' }}>{t('common.success')}</h3>
                                </div>
                            )}

                            {verificationState === 'error' && (
                                <div>
                                    <div className="icon-shake">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="icon-svg error-icon" viewBox="0 0 24 24" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <line x1="18" y1="6" x2="6" y2="18"></line>
                                            <line x1="6" y1="6" x2="18" y2="18"></line>
                                        </svg>
                                    </div>
                                    <h3 style={{ marginTop: '20px', color: '#ef4444', fontWeight: 800 }}>{t('common.invalidCode')}</h3>
                                    <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>{t('common.checkCodeTryAgain')}</p>
                                </div>
                            )}

                            {verificationState === 'input' && (
                                <button className="btn-primary auth-submit" onClick={startVerificationProcess}>{t('common.verifyCode')}</button>
                            )}

                            <div className="auth-switch-row">
                                <span>{t('login.rememberedPassword')}</span>
                                <button type="button" className="auth-link-button" onClick={() => setView('login')}>{t('common.logIn')}</button>
                            </div>
                        </>
                    )}

                    {view === 'resetting' && (
                        <>
                            <input
                                type="password"
                                placeholder={t('login.newPasswordPlaceholder')}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={resetLoading}
                            />
                            <input
                                type="password"
                                placeholder={t('login.confirmNewPasswordPlaceholder')}
                                value={confirmNewPassword}
                                onChange={(e) => setConfirmNewPassword(e.target.value)}
                                disabled={resetLoading}
                            />

                            <button
                                className="btn-primary auth-submit"
                                onClick={handleResetPassword}
                                disabled={resetLoading || !newPassword || !confirmNewPassword}
                            >
                                {resetLoading ? t('login.resettingPassword') : t('login.resetPasswordButton')}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

export default Login;
